import { useTranslations } from 'next-intl';
import { Loader } from '@/components/base/Layout/Loader';
import { Link } from '@/i18n/routing';

export function ButtonMain({
  disabled,
  loading,
  onClick,
  icon,
  iconLeft,
  hideTextSm,
  children,
  size = 'lg',
  theme = 'filled',
  className = '',
  dataTestId,
  tag,
  href,
  target,
}) {
  const t = useTranslations();
  const Tag = tag === 'Link' ? Link : 'button';
  let textSize;
  let height;
  let gaps;
  switch (size) {
    case 'sm':
      textSize = 'text-sm';
      height = 'h-9';
      gaps = icon
        ? 'gap-2 pl-[0.875rem] pr-3'
        : 'gap-2 pl-[0.875rem] pr-[0.875rem]';
      break;
    case 'md':
      textSize = 'text-sm';
      height = 'h-11';
      height = hideTextSm ? 'max-sm:h-9 sm:h-11' : 'h-11';
      gaps =
        (icon && !iconLeft && !hideTextSm && 'gap-2 pl-6 pr-4') ||
        (hideTextSm &&
          iconLeft &&
          'max-sm:p-1.5 sm:gap-2 sm:pr-6 sm:pl-4 sm:py-1') ||
        iconLeft
          ? 'gap-2 pr-6 pl-4'
          : 'gap-2 pl-6 pr-6';
      break;
    case 'lg':
    default:
      textSize = 'text-lg';
      height = 'h-14';
      gaps = icon ? 'gap-2 pl-[1.5rem] pr-4' : 'gap-2 pl-[1.5rem] pr-[1.5rem]';
      break;
  }
  let textColor;
  let border;
  let bg;
  switch (theme) {
    case 'chip':
      textColor =
        'text-slate-500 hover:text-slate-500 focus:text-white active:text-white disabled:text-slate-400';
      border = 'border border-slate-300 active:border-primary-800 focus:border-primary-800 !rounded-[14px] !px-[18px]';
      bg =
        'bg-transparent hover:bg-teal-50 focus:bg-primary-800 focus:outline-none active:bg-primary-800 disabled:bg-slate-200 transition-colors duration-150 ease-in';
      break;
    case 'filled':
      textColor =
        'text-white hover:text-white focus:text-white disabled:text-slate-400';
      border = 'border-none';
      bg =
        'bg-primary-800 hover:bg-primary-600 focus:bg-primary-500 focus:outline-none active:bg-primary-800 disabled:bg-slate-200';
      break;
    case 'amber':
      textColor = '!text-white disabled:!text-teal-500';
      border = 'border-none';
      bg =
        'bg-amber-400 hover:bg-amber-500 focus:bg-amber-600 focus:outline-none active:bg-amber-400 disabled:bg-teal-700';
      break;
    case 'outlined':
      textColor =
        'text-primary-800 hover:text-primary-600 focus:text-primary-500 disabled:text-slate-400';
      border =
        'border border-primary-800 hover:border-primary-600 focus:border-primary-500 disabled:border-slate-200';
      bg =
        'bg-transparent hover:bg-transparent focus:bg-transparent focus:outline-none active:bg-transparent disabled:bg-transparent';
      break;
    case 'text':
    default:
      textColor =
        'text-primary-800 hover:text-primary-600 focus:text-primary-500 disabled:text-slate-400';
      border = 'border-none';
      bg =
        'bg-transparent hover:bg-primary-50 focus:bg-primary-100 focus:outline-none active:bg-transparent disabled:bg-transparent';
      break;
  }
  return (
    <Tag
      className={`
      relative
      flex items-center justify-center flex-none
      font-semibold ${textColor} ${textSize}
      ${height}
      ${border} rounded-lg
      ${bg}
      ${gaps}
      ${className}
      `}
      href={href}
      target={target}
      aria-label={t(children)}
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={dataTestId}
    >
      <span
        className={`${loading ? 'invisible' : ''} ${iconLeft ? 'order-2' : ''} ${hideTextSm ? 'max-sm:hidden' : ''}`}
      >
        {children}
      </span>
      {icon && (
        <i
          className={`${icon} text-[24px] ${loading ? 'invisible' : ''} ${iconLeft ? 'order-1' : ''}`}
        />
      )}
      {loading && <Loader />}
    </Tag>
  );
}
